import { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import type { PdfRef } from "src/components/NativePdfView"
import { NativePdfView as Pdf } from "src/components/NativePdfView"
import { SidekickView } from "src/components/SidekickView"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { backToRoot } from "src/utils/routerHelpers"

// Minimum uniform scale of the cluster when fully collapsed (10%).
const MIN_CLUSTER_SCALE = 0.1

export default function FileViewerScreen() {
  const {
    url: urlParam,
    title: titleParam,
    resourceId,
    taskId,
    startPage: startPageParam,
  } = useLocalSearchParams<{
    url?: string | string[]
    title?: string | string[]
    resourceId?: string
    taskId?: string
    startPage?: string
  }>()
  const startPage = startPageParam ? parseInt(startPageParam, 10) : undefined
  const resolvedUrl = Array.isArray(urlParam) ? urlParam[0] : urlParam
  const resolvedTitle = Array.isArray(titleParam) ? titleParam[0] : titleParam
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { theme, navTheme } = useAppTheme()
  const { colors } = theme
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const pdfRef = useRef<PdfRef>(null)
  const hasNavigatedToStartPage = useRef(false)

  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [numPages, setNumPages] = useState(0)

  const { setMediaContext, clearMediaContext, updateOffset, updateTotalLength } = useMediaContext()

  // ── Page-driven collapse (Safari-style) ───────────────────────────────────
  // react-native-pdf doesn't expose a continuous scroll offset (unlike the
  // WebView used by the web browser), so we drive the collapse from page
  // navigation instead: advancing a page collapses the cluster (scale 100% →
  // 10%, opacity 100% → 0%); going back a page expands it again. Tapping the
  // shrunken cluster, or switching to type mode, also restores it.
  const collapseProgress = useRef(new Animated.Value(0)).current
  // Seed with the requested start page so the programmatic jump to it on load
  // doesn't read as a forward page change (which would collapse immediately).
  const lastPage = useRef(startPage ?? 1)
  // Gate that suppresses collapse while the document loads / performs its
  // initial start-page navigation. Armed shortly after load settles.
  const collapseArmed = useRef(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const clusterScale = useRef(
    collapseProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [1, MIN_CLUSTER_SCALE],
    }),
  ).current
  const clusterOpacity = useRef(
    collapseProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 0],
    }),
  ).current

  const animateCollapse = useCallback(
    (collapsed: boolean) => {
      setIsCollapsed(collapsed)
      Animated.timing(collapseProgress, {
        toValue: collapsed ? 1 : 0,
        duration: 200,
        useNativeDriver: false,
      }).start()
    },
    [collapseProgress],
  )

  const expandControls = useCallback(() => {
    animateCollapse(false)
  }, [animateCollapse])

  const handleModeChange = useCallback(
    (mode: "idle" | "talk" | "type") => {
      // Bring the cluster back to full size when the user starts typing so the
      // input isn't stuck shrunken after paging through the document.
      if (mode === "type") expandControls()
    },
    [expandControls],
  )

  // Unlock orientation for file viewer
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Register media context for context-aware chat responses
  useEffect(() => {
    if (resourceId) {
      setMediaContext({
        resourceId,
        taskId,
        title: resolvedTitle,
        initialOffset: String(startPage ?? 1),
      })
    }
    return () => {
      clearMediaContext()
    }
  }, [resourceId, taskId, resolvedTitle, setMediaContext, clearMediaContext, startPage])

  useEffect(() => {
    if (numPages > 0) {
      updateTotalLength(String(numPages))
    }
  }, [numPages, updateTotalLength])

  // Handle Android navigation bar
  useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setVisibilityAsync("hidden")
      NavigationBar.setBehaviorAsync("overlay-swipe")
      NavigationBar.setBackgroundColorAsync("#000000")
      NavigationBar.setButtonStyleAsync("light")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setVisibilityAsync("visible")
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, navTheme.dark])

  const handleLoadComplete = useCallback((numberOfPages: number) => {
    setNumPages(numberOfPages)
    setIsLoading(false)
    setHasError(false)
  }, [])

  const handlePageChanged = useCallback(
    (page: number) => {
      updateOffset(String(page))
      const prev = lastPage.current
      lastPage.current = page
      if (!collapseArmed.current) return
      // Advancing through the document collapses the cluster; paging back
      // brings it back into view (mirrors the web browser's scroll behavior).
      if (page > prev) {
        animateCollapse(true)
      } else if (page < prev) {
        animateCollapse(false)
      }
    },
    [updateOffset, animateCollapse],
  )

  const handleError = useCallback((error: object) => {
    console.warn("[FileViewer] PDF load error:", error)
    setIsLoading(false)
    setHasError(true)
  }, [])

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || (numPages > 0 && page > numPages)) return
      pdfRef.current?.setPage(page)
    },
    [numPages],
  )

  // Navigate to startPage once the PDF has finished loading
  useEffect(() => {
    if (startPage && numPages > 0 && !hasNavigatedToStartPage.current) {
      hasNavigatedToStartPage.current = true
      goToPage(startPage)
    }
  }, [startPage, numPages, goToPage])

  // Arm the page-driven collapse only after the document has loaded and any
  // programmatic start-page navigation has had time to settle, so the initial
  // jump doesn't shrink the cluster before the user starts reading.
  useEffect(() => {
    if (isLoading || numPages === 0) return
    const timeout = setTimeout(() => {
      collapseArmed.current = true
    }, 400)
    return () => clearTimeout(timeout)
  }, [isLoading, numPages])

  if (!resolvedUrl) {
    return (
      <View style={styles.errorContainer}>
        <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
        <Text style={styles.errorText}>No file URL provided.</Text>
        <TouchableOpacity style={styles.backButton} onPress={() => backToRoot()}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Close button - above SidekickView */}
      <View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
          },
        ]}
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close file viewer"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          darkBackground={false}
          showControlsBarBackground={false}
          controlsOpacity={clusterOpacity}
          controlsScale={clusterScale}
          controlsVisible={true}
          controlsCollapsed={isCollapsed}
          onExpandControls={expandControls}
          onModeChange={handleModeChange}
          riveOwner="video"
        >
          {/* File viewer content */}
          <View style={styles.pdfContainer}>
            {isLoading && (
              <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
                <MaterialIcons name="hourglass-empty" size={32} color={colors.text} />
                <Text style={[styles.loadingText, { color: colors.text }]}>
                  Loading document...
                </Text>
              </View>
            )}
            {hasError && (
              <View style={styles.errorOverlay}>
                <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
                <Text style={styles.errorTitle}>Failed to Load PDF</Text>
                <Text style={styles.errorText}>
                  The PDF could not be loaded. Please check that the file URL is accessible.
                </Text>
              </View>
            )}
            <Pdf
              ref={pdfRef}
              source={{ uri: resolvedUrl, cache: true }}
              style={[styles.pdf, hasError && styles.hidden]}
              trustAllCerts={false}
              onLoadComplete={handleLoadComplete}
              onPageChanged={handlePageChanged}
              onError={handleError}
              enablePaging={false}
              spacing={8}
            />
          </View>
        </SidekickView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000000",
  },
  closeButton: {
    position: "absolute",
    zIndex: 2100,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#000000",
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
    marginTop: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  errorText: {
    fontSize: 14,
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 8,
  },
  backButton: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  backButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  pdfContainer: {
    flex: 1,
  },
  pdf: {
    flex: 1,
    backgroundColor: "#000000",
  },
  hidden: {
    opacity: 0,
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    zIndex: 1,
  },
  loadingText: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 12,
  },
  errorOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    padding: 24,
    zIndex: 2,
  },
})
